class GrowthlabFinder extends HTMLElement {
  async connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    try {
      const { recommendRack } = await import(this.dataset.coreUrl);
      this.recommend = recommendRack;
      this.data = JSON.parse(this.querySelector('[data-finder-data]').textContent);
      this.steps = [...this.querySelectorAll('[data-finder-step]')];
      this.index = 0;
      this.form = this.querySelector('form');
      this.addEventListener('change', () => this.update());
      this.querySelector('[data-back]').addEventListener('click', () => { this.index--; this.update(true); });
      this.querySelector('[data-restart]').addEventListener('click', () => this.editAnswers(true));
      this.querySelector('[data-edit]').addEventListener('click', () => this.editAnswers(false));
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        if (!this.steps[this.index].querySelector('input:checked')) return;
        if (this.index < this.steps.length - 1) { this.index++; this.update(true); }
        else this.showResult();
      });
      this.update();
      this.form.hidden = false;
      this.querySelector('[data-finder-loading]').hidden = true;
    } catch {
      this.initialized = false;
      this.querySelector('[data-finder-loading]').hidden = false;
    }
  }

  editAnswers(reset) {
    if (reset) this.form.reset();
    this.index = 0;
    this.classList.remove('rf--results');
    this.querySelector('[data-result]').hidden = true;
    this.form.hidden = false;
    this.update(true);
  }

  focusPanel(element) {
    element.focus({ preventScroll: true });
    element.scrollIntoView({ block: 'start', behavior: 'auto' });
  }

  update(focus = false) {
    this.steps.forEach((step, i) => { step.hidden = this.index !== i; });
    const next = this.querySelector('[data-next]');
    next.disabled = !this.steps[this.index].querySelector('input:checked');
    next.textContent = this.index === this.steps.length - 1 ? this.data.labels.results : this.data.labels.next;
    this.querySelector('[data-back]').hidden = this.index === 0;
    this.querySelector('[data-progress]').textContent = this.data.labels.progress.replace('__STEP__', this.index + 1);
    this.querySelector('progress').value = this.index + 1;
    if (focus) this.focusPanel(this.steps[this.index].querySelector('legend'));
  }

  showResult() {
    const answers = Object.fromEntries(new FormData(this.form));
    const result = this.recommend(answers, this.data.products);
    if (result.type === 'incomplete') return;
    const box = this.querySelector('[data-result]');
    const list = this.querySelector('[data-product-list]');
    list.replaceChildren();
    const isProduct = result.type === 'product';
    this.querySelector('[data-help-result]').hidden = isProduct;
    this.querySelector('[data-fit-note]').hidden = !isProduct;
    this.querySelector('[data-partial-note]').hidden = !result.partialCapacity;
    this.querySelector('[data-measure-note]').hidden = !result.needsMeasurement;
    this.querySelector('[data-narrow-note]').hidden = !result.narrow;
    this.querySelector('[data-answer-summary]').textContent = this.steps.map(step => step.querySelector('input:checked').closest('label').querySelector('strong').textContent).join(' · ');
    const matches = isProduct ? [result, ...result.alternatives] : result.suggestions || [];
    this.querySelector('[data-explore-note]').hidden = isProduct || !matches.length;
    this.querySelector('[data-match-count]').textContent = isProduct ? this.data.labels.match_count.replace('__COUNT__', matches.length) : '';
    for (const item of matches) {
      const card = this.productCard(item);
      if (!isProduct) {
        card.querySelector('[data-result-reason]').textContent = this.data.labels.explore_reason;
        const directCart = card.querySelector('growthlab-cart-extras');
        if (directCart) directCart.remove();
      }
      list.append(card);
    }
    if (!isProduct) this.querySelector('[data-help-reason]').textContent = this.data.labels[result.reason];
    this.form.hidden = true;
    this.classList.add('rf--results');
    box.hidden = false;
    this.focusPanel(box.querySelector('h2'));
  }

  productCard(item) {
    const card = this.querySelector('[data-product-template]').content.firstElementChild.cloneNode(true);
    const { product, variant, capacity, width } = item;
    card.querySelector('[data-result-title]').textContent = this.productName(product);
    card.querySelector('[data-result-variant]').textContent = variant.title;
    card.querySelector('[data-result-reason]').textContent = this.data.labels[product.kind + '_reason'];
    card.querySelector('[data-result-capacity]').textContent = this.data.labels.capacity.replace('__PAIRS__', capacity);
    card.querySelector('[data-result-width]').textContent = this.data.labels.width.replace('__WIDTH__', width);
    card.querySelector('[data-result-price]').textContent = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency }).format(variant.price / 100);
    const image = card.querySelector('[data-result-image]');
    const imageUrl = new URL(variant.featured_image?.src || product.image, location.origin);
    imageUrl.searchParams.set('width', '600');
    image.src = imageUrl.href;
    image.alt = this.productName(product) + ' — ' + variant.title;
    const url = new URL(product.url, location.origin);
    if (product.view) url.searchParams.set('view', product.view);
    url.searchParams.set('variant', variant.id);
    card.querySelectorAll('[data-product-link]').forEach(link => { link.href = url.href; });
    const cartButton = card.querySelector('[data-extra-add]');
    if (cartButton) {
      cartButton.dataset.extraAdd = variant.id;
      cartButton.disabled = !variant.available;
    }
    return card;
  }

  productName(product) {
    return product.kind === 'titan' ? this.data.labels.titan_name.replace('__WIDTH__', product.width) : product.name;
  }
}

if (!customElements.get('growthlab-finder')) customElements.define('growthlab-finder', GrowthlabFinder);
